import { useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useMultilocLines } from '@/hooks/useMultiloc'
import { fetchMultiloc } from '@/api/multiloc'
import { mapApiToRows } from './types'
import type { MultilocRow } from './types'

interface CopyMultilocDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  currentLineId: string
  onCopy: (rows: MultilocRow[]) => void
}

export function CopyMultilocDialog({
  open,
  onOpenChange,
  planId,
  versionId,
  currentLineId,
  onCopy,
}: CopyMultilocDialogProps) {
  const [isCopying, setIsCopying] = useState(false)
  const [search, setSearch] = useState('')

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useMultilocLines(
      open ? planId : undefined,
      open ? versionId : undefined,
      search
    )

  const lines = (data?.pages ?? [])
    .flatMap(page => page.data)
    .filter(line => line.id !== currentLineId)

  const handleSelect = async (lineId: string) => {
    setIsCopying(true)
    try {
      const response = await fetchMultiloc(planId, versionId, lineId)
      const rows = mapApiToRows(response.items)
      onCopy(rows)
      onOpenChange(false)
    } finally {
      setIsCopying(false)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSearch('')
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Copy from another SKU</DialogTitle>
          <DialogDescription>
            Select a line item to copy its presentation model configuration.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by SKU or description..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-[350px] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && lines.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No other lines with presentation models found.
            </p>
          )}

          {!isLoading && lines.length > 0 && (
            <div className="space-y-1">
              {lines.map(line => (
                <Button
                  key={line.id}
                  variant="ghost"
                  className="w-full justify-start h-auto py-3 px-3 cursor-pointer"
                  disabled={isCopying}
                  onClick={() => handleSelect(line.id)}
                >
                  <div className="text-left">
                    <p className="text-sm font-medium">
                      {line.description || 'Untitled'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      SKU {line.sku_number || '-'}
                    </p>
                  </div>
                </Button>
              ))}

              {hasNextPage && (
                <div className="flex justify-center pt-2 pb-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer text-muted-foreground"
                    disabled={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                  >
                    {isFetchingNextPage ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Load more
                  </Button>
                </div>
              )}
            </div>
          )}

          {isCopying && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
              <span className="text-sm text-muted-foreground">
                Copying configuration...
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
